import { useEffect, useMemo, useState, useCallback, useRef } from 'react';

/* ── helpers ─────────────────────────────────────────────────────────────── */
const formatCurrency = (v) =>
  v ? `₹${Number(v).toLocaleString('en-IN')}` : '-';

const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10);
  const [y, m, dd] = s.split('-');
  return `${dd}/${m}/${y}`;
};

const BUCKET_COLORS = {
  HOT: '#dc2626',
  WARM: '#ea580c',
  COLD: '#2563eb',
  VIP: '#7c3aed',
  AT_RISK: '#991b1b',
};

const EVENT_TYPES = [
  'WEDDING','CORPORATE','BIRTHDAY','SOCIAL','MICE','CONFERENCE',
  'EXHIBITION','RECEPTION','ENGAGEMENT','OTHER',
];

const BUCKETS = ['HOT','WARM','COLD','VIP','AT_RISK'];

/* ══════════════════════════════════════════════════════════════════════════ */

function BanquetPipeline({ token, selectedProperty, onNavigateToDetail }) {
  const chainId  = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-chain-id': chainId,
      'x-property-id': propertyId,
    }),
    [token, chainId, propertyId],
  );

  /* ── state ──────────────────────────────────────────────────────────── */
  const [stages, setStages]       = useState([]);        // pipeline metadata
  const [leadsByStage, setLeadsByStage] = useState({});  // { STAGE_KEY: [lead,...] }
  const [users, setUsers]         = useState([]);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [dragOverStage, setDragOverStage] = useState(null);
  const dragData = useRef(null);                         // { leadId, fromStage }

  const [filters, setFilters] = useState({
    assigned_to: '',
    event_type: '',
    lead_bucket: '',
  });

  /* ── API helpers ────────────────────────────────────────────────────── */
  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  /* ── load users (for filter dropdown) ───────────────────────────────── */
  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/core/users', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setUsers(Array.isArray(data) ? data : []);
    } catch { /* swallow */ }
  }, [headers]);

  /* ── load pipeline metadata ─────────────────────────────────────────── */
  const loadPipelineMetadata = useCallback(async () => {
    const res = await fetch('/api/v1/banquet-crm/pipeline', { headers });
    const { data, error: apiErr } = await parseJson(res);
    if (!res.ok) throw new Error(apiErr || 'Failed to load pipeline');
    return data.stages || [];
  }, [headers]);

  /* ── load leads for ONE stage ───────────────────────────────────────── */
  const loadLeadsForStage = useCallback(async (stageKey) => {
    const params = new URLSearchParams({
      pipeline_stage: stageKey,
      limit: '50',
      sort_by: 'score_overall',
      sort_order: 'DESC',
    });
    if (filters.assigned_to) params.set('assigned_to', filters.assigned_to);
    if (filters.event_type)  params.set('event_type', filters.event_type);
    if (filters.lead_bucket) params.set('lead_bucket', filters.lead_bucket);

    const res = await fetch(`/api/v1/banquet-crm/leads?${params}`, { headers });
    const { data } = await parseJson(res);
    return res.ok ? (data.leads || []) : [];
  }, [headers, filters]);

  /* ── full load: metadata + all stage leads ──────────────────────────── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const stagesData = await loadPipelineMetadata();
      setStages(stagesData);

      const activeStages = stagesData.filter((s) => s.is_active);
      const results = await Promise.all(
        activeStages.map(async (s) => ({
          key: s.stage_key,
          leads: await loadLeadsForStage(s.stage_key),
        })),
      );

      const map = {};
      results.forEach((r) => { map[r.key] = r.leads; });
      setLeadsByStage(map);
    } catch (err) {
      setError(err?.message || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [loadPipelineMetadata, loadLeadsForStage]);

  /* ── initial + filter-change load ───────────────────────────────────── */
  useEffect(() => {
    if (chainId && propertyId) {
      loadAll();
      loadUsers();
    }
  }, [chainId, propertyId, loadAll, loadUsers]);

  /* ── drag & drop handlers ──────────────────────────────────────────── */
  const handleDragStart = (e, leadId, fromStage) => {
    dragData.current = { leadId, fromStage };
    e.dataTransfer.effectAllowed = 'move';
    // semi-transparent ghost
    if (e.target) e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    if (e.target) e.target.style.opacity = '1';
    setDragOverStage(null);
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const { leadId, fromStage } = dragData.current || {};
    if (!leadId || fromStage === targetStage) return;

    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/stage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ stage: targetStage }),
      });
      if (!res.ok) {
        const { error: apiErr } = await parseJson(res);
        throw new Error(apiErr || 'Stage change failed');
      }
      // optimistic: move the card locally then refresh in background
      setLeadsByStage((prev) => {
        const next = { ...prev };
        const fromList = (next[fromStage] || []).filter((l) => String(l.lead_id) !== String(leadId));
        const movedLead = (prev[fromStage] || []).find((l) => String(l.lead_id) === String(leadId));
        const toList = [...(next[targetStage] || [])];
        if (movedLead) toList.unshift({ ...movedLead, pipeline_stage: targetStage });
        next[fromStage] = fromList;
        next[targetStage] = toList;
        return next;
      });
      // refresh metadata counts in background
      loadPipelineMetadata().then(setStages).catch(() => {});
    } catch (err) {
      setError(err?.message || 'Failed to move lead');
      loadAll(); // re-sync on error
    }
  };

  /* ── derived: ordered visible stages ───────────────────────────────── */
  const activeStages = useMemo(
    () => stages.filter((s) => s.is_active),
    [stages],
  );
  const normalStages = useMemo(
    () => activeStages.filter((s) => !['CLOSED_WON', 'LOST'].includes(s.stage_key)),
    [activeStages],
  );
  const wonStage  = activeStages.find((s) => s.stage_key === 'CLOSED_WON');
  const lostStage = activeStages.find((s) => s.stage_key === 'LOST');

  /* helper: compute total budget for a stage from leads */
  const stageTotal = (stageKey) =>
    (leadsByStage[stageKey] || []).reduce((s, l) => s + Number(l.budget_max || 0), 0);

  /* ── filter change handler ──────────────────────────────────────────── */
  const onFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /* ══════════════════════════════════════════════════════════════════════ */
  /*  STYLES                                                               */
  /* ══════════════════════════════════════════════════════════════════════ */

  const S = {
    page: {
      padding: 16,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    topBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      flexWrap: 'wrap',
      gap: 8,
    },
    title: {
      fontSize: 20,
      fontWeight: 700,
      color: '#1e293b',
      margin: 0,
    },
    refreshBtn: {
      padding: '6px 14px',
      fontSize: 13,
      border: '1px solid #cbd5e1',
      borderRadius: 6,
      background: '#fff',
      cursor: 'pointer',
      color: '#334155',
    },
    filtersRow: {
      display: 'flex',
      gap: 10,
      marginBottom: 14,
      flexWrap: 'wrap',
    },
    filterSelect: {
      padding: '6px 10px',
      fontSize: 13,
      border: '1px solid #cbd5e1',
      borderRadius: 6,
      background: '#fff',
      color: '#334155',
      minWidth: 150,
    },
    errorBar: {
      marginBottom: 10,
      padding: '10px 14px',
      background: '#fef2f2',
      color: '#b91c1c',
      borderRadius: 6,
      fontSize: 13,
    },
    board: {
      display: 'flex',
      gap: 14,
      overflowX: 'auto',
      flex: 1,
      paddingBottom: 12,
      minHeight: 0,
    },
    col: (color, isDragOver) => ({
      flexShrink: 0,
      minWidth: 280,
      width: 280,
      background: isDragOver ? '#e0e7ff' : '#f1f5f9',
      borderRadius: 10,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100%',
      transition: 'background 0.15s',
      border: isDragOver ? '2px dashed #6366f1' : '2px solid transparent',
    }),
    colHeaderBase: {
      padding: '12px 14px 10px',
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
      borderBottom: '3px solid',
    },
    colTitle: { fontWeight: 600, fontSize: 14, margin: 0, display: 'flex', alignItems: 'center', gap: 8 },
    countBadge: {
      background: '#fff',
      borderRadius: 12,
      padding: '1px 9px',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '20px',
    },
    colBudget: { fontSize: 12, marginTop: 4, opacity: 0.85 },
    colCards: {
      flex: 1,
      overflowY: 'auto',
      padding: '8px 8px 4px',
    },
    card: (slaBreach) => ({
      background: '#fff',
      borderRadius: 8,
      padding: '10px 12px',
      marginBottom: 8,
      cursor: 'grab',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      borderLeft: slaBreach ? '4px solid #dc2626' : '4px solid transparent',
      transition: 'box-shadow 0.15s',
      userSelect: 'none',
    }),
    cardName: { fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 4 },
    cardRow: { fontSize: 12, color: '#64748b', marginBottom: 2, display: 'flex', gap: 6, alignItems: 'center' },
    bucketBadge: (bucket) => ({
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      color: '#fff',
      background: BUCKET_COLORS[bucket] || '#64748b',
    }),
    scoreBadge: {
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      color: '#fff',
      background: '#0f766e',
    },
    emptyCol: {
      textAlign: 'center',
      color: '#94a3b8',
      fontSize: 13,
      padding: '24px 8px',
    },
  };

  /* ══════════════════════════════════════════════════════════════════════ */
  /*  RENDER HELPERS                                                       */
  /* ══════════════════════════════════════════════════════════════════════ */

  const renderLeadCard = (lead, stageKey) => {
    const slaBreach = !!lead.sla_breached;
    const eventRange =
      lead.event_from_date
        ? lead.event_to_date && lead.event_to_date !== lead.event_from_date
          ? `${fmtDate(lead.event_from_date)} - ${fmtDate(lead.event_to_date)}`
          : fmtDate(lead.event_from_date)
        : '';

    return (
      <div
        key={lead.lead_id}
        style={S.card(slaBreach)}
        draggable="true"
        onDragStart={(e) => handleDragStart(e, lead.lead_id, stageKey)}
        onDragEnd={handleDragEnd}
        onClick={() => onNavigateToDetail && onNavigateToDetail(lead.lead_id)}
        title={slaBreach ? 'SLA breached — action overdue' : ''}
      >
        {/* client name */}
        <div style={S.cardName}>{lead.client_name || 'Unnamed'}</div>

        {/* event type + name */}
        {(lead.event_type || lead.event_name) && (
          <div style={S.cardRow}>
            {lead.event_type && <span>{lead.event_type.replace(/_/g, ' ')}</span>}
            {lead.event_name && <span style={{ color: '#475569' }}>— {lead.event_name}</span>}
          </div>
        )}

        {/* event date range */}
        {eventRange && (
          <div style={S.cardRow}>
            <span style={{ fontSize: 13 }}>📅</span>
            <span>{eventRange}</span>
          </div>
        )}

        {/* pax + budget */}
        <div style={{ ...S.cardRow, justifyContent: 'space-between' }}>
          {lead.expected_pax ? <span>Pax: {lead.expected_pax}</span> : <span />}
          <span style={{ fontWeight: 600, color: '#334155' }}>{formatCurrency(lead.budget_max)}</span>
        </div>

        {/* badges row */}
        <div style={{ ...S.cardRow, marginTop: 4, gap: 6, flexWrap: 'wrap' }}>
          {lead.lead_bucket && (
            <span style={S.bucketBadge(lead.lead_bucket)}>
              {lead.lead_bucket.replace(/_/g, ' ')}
            </span>
          )}
          {lead.score_overall != null && (
            <span style={S.scoreBadge}>{lead.score_overall}</span>
          )}
          {slaBreach && (
            <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>SLA BREACH</span>
          )}
        </div>

        {/* assigned */}
        {lead.assigned_to_name && (
          <div style={{ ...S.cardRow, marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
            {lead.assigned_to_name}
          </div>
        )}
      </div>
    );
  };

  const renderColumn = (stage, overrideBg, overrideTextColor) => {
    const stageKey = stage.stage_key;
    const leads = leadsByStage[stageKey] || [];
    const count = leads.length;
    const total = stageTotal(stageKey);
    const color = overrideBg || stage.stage_color || '#64748b';
    const isDragOver = dragOverStage === stageKey;

    return (
      <div
        key={stageKey}
        style={S.col(color, isDragOver)}
        onDragOver={(e) => handleDragOver(e, stageKey)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, stageKey)}
      >
        {/* header */}
        <div
          style={{
            ...S.colHeaderBase,
            background: `${color}22`,
            borderColor: color,
            color: overrideTextColor || '#1e293b',
          }}
        >
          <div style={S.colTitle}>
            <span>{stage.stage_name}</span>
            <span style={S.countBadge}>{count}</span>
          </div>
          <div style={S.colBudget}>{formatCurrency(total)}</div>
        </div>

        {/* cards */}
        <div style={S.colCards}>
          {count === 0 ? (
            <div style={S.emptyCol}>No leads</div>
          ) : (
            leads.map((l) => renderLeadCard(l, stageKey))
          )}
        </div>
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════════════════ */
  /*  RENDER                                                               */
  /* ══════════════════════════════════════════════════════════════════════ */

  return (
    <div style={S.page}>
      {/* top bar */}
      <div style={S.topBar}>
        <h2 style={S.title}>Pipeline View</h2>
        <button style={S.refreshBtn} onClick={loadAll}>
          ↻ Refresh
        </button>
      </div>

      {/* filters */}
      <div style={S.filtersRow}>
        <select
          style={S.filterSelect}
          value={filters.assigned_to}
          onChange={(e) => onFilterChange('assigned_to', e.target.value)}
        >
          <option value="">All Reps</option>
          {users.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.name_of_user || u.username}
            </option>
          ))}
        </select>

        <select
          style={S.filterSelect}
          value={filters.event_type}
          onChange={(e) => onFilterChange('event_type', e.target.value)}
        >
          <option value="">All Event Types</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <select
          style={S.filterSelect}
          value={filters.lead_bucket}
          onChange={(e) => onFilterChange('lead_bucket', e.target.value)}
        >
          <option value="">All Buckets</option>
          {BUCKETS.map((b) => (
            <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* error */}
      {error && <div style={S.errorBar}>{error}</div>}

      {/* board */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Loading...</div>
      ) : (
        <div style={S.board}>
          {normalStages.map((stage) => renderColumn(stage))}

          {wonStage &&
            renderColumn(
              { ...wonStage, stage_name: 'Won' },
              '#16a34a',
              '#14532d',
            )}

          {lostStage &&
            renderColumn(
              { ...lostStage, stage_name: 'Lost' },
              '#dc2626',
              '#7f1d1d',
            )}
        </div>
      )}
    </div>
  );
}

export default BanquetPipeline;
